import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { UserRole } from '../enums/user-role';
import { UserProfile } from '../models/user-profile';
import { ConfigurationService } from '../services/configuration.service';
import { HttpOptionsHelper } from '../helper/http-options.helper';
import { BaseApiService } from '../services/base-api.service';
import { Tenant } from '../models/tenant';
import { OscOptions } from './osc-options';


@Injectable({ providedIn: 'root' })
export class AuthService {
  public currentUser: Observable<UserProfile>;
  public currentUserTenant: Observable<Tenant>;
  private userProfileSubject: BehaviorSubject<UserProfile>;
  private currentUserTenantSubject: BehaviorSubject<Tenant>;
  private isLoggedInSubject: BehaviorSubject<boolean>;
  private options: OscOptions  = {
    headers: { 'Authorization': '',
               'Access-Control-Allow-Origin': '*',
               'Access-Control-Allow-Headers': 'Content-Type',
               'Access-Control-Allow-Methods': 'GET, POST, PUT, DELETE, OPTIONS'
              }
  };


  constructor(private httpClient: HttpClient,
    private configurationService: ConfigurationService,
    private baseApiService: BaseApiService) {
    this.userProfileSubject = new BehaviorSubject<UserProfile>(null);
    this.currentUserTenantSubject = new BehaviorSubject<Tenant>(null);
    this.isLoggedInSubject = new BehaviorSubject<boolean>(false);
    this.currentUser = this.userProfileSubject.asObservable();
    this.currentUserTenant = this.currentUserTenantSubject.asObservable();
  }

  public login(): void {
  let loginUrl = this.getAuthenticationEndpointUrl() + 'login';
  const callbackUrl = this.getCallBackUrl();
  if (callbackUrl) {
    loginUrl += `?callbackUrl=${callbackUrl}`;
  }
  window.location.href = loginUrl;
}

  public logout(): void {
  const logoutUrl = this.getAuthenticationEndpointUrl() + 'logout';

  this.setIdToken(null);
  this.setRefreshToken(null);
  localStorage.setItem('logout-event', `logout ${new Date().toUTCString()}`);

  this.userProfileSubject.next(null);
  this.isLoggedInSubject.next(false);

  window.location.href = logoutUrl;
}

  public refreshTokens(): Observable<void> {
  const options = HttpOptionsHelper.getOptions();

  return new Observable((observer) => {
    this.httpClient
      .get<void>(this.getAuthenticationEndpointUrl() + 'refresh-tokens', options)
      .subscribe({
        next: () => observer.next(),
        error: (err) => observer.error(err),
        complete: () => observer.complete()
      });
  });
}


  public isLoggedIn(): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      this.isLoggedInSubject.value ?
        resolve(this.isLoggedInSubject.value) :
        this.httpClient.get<boolean>(this.getAuthenticationEndpointUrl() + 'user/isAuthenticated',
          HttpOptionsHelper.getOptions())
          .toPromise().then(
            (value: boolean) => {
              this.isLoggedInSubject.next(value);
              resolve(value);
            }, (err) => {
              this.isLoggedInSubject.next(false);
              resolve(false);
            }
          );
    });
  }

  public getCurrentUserProfile(): Promise<UserProfile> {
    return new Promise((resolve, reject) => {
      const user = this.userProfileSubject.value;
      user ?
        resolve(user) :
        this.httpClient.get<UserProfile>(this.getAuthenticationEndpointUrl() + 'user/profile',
          HttpOptionsHelper.getOptions())
          .toPromise()
          .then((u: UserProfile) => {
            const userProfileResult = Object.assign(new UserProfile({ baseApiService: this.baseApiService }), u);
            this.userProfileSubject.next(userProfileResult);
            resolve(userProfileResult);
          })
          .catch((err) => {
            this.userProfileSubject.next(null);
            reject(err);
          });
    });
  }

  public getCurrentUserTenant(): Promise<Tenant> {
    return new Promise((resolve, reject) => {
      const tenant = this.currentUserTenantSubject.value;
      tenant ?
        resolve(tenant) :
        this.httpClient.get<Tenant>(this.getAuthenticationEndpointUrl() + 'user/tenant',
          HttpOptionsHelper.getOptions())
          .toPromise()
          .then((t: Tenant) => {
            this.currentUserTenantSubject.next(t);
            resolve(t);
          })
          .catch((err) => {
            this.currentUserTenantSubject.next(null);
            reject(err);
          });
    });
  }

  public get currentUserProfile(): UserProfile {
    return this.userProfileSubject.value;
  }

  public reloadPageOnLogout(): void {
    window.addEventListener('storage', (event) => {
      if (event.key === 'logout-event') {
        location.reload();
      }
    });
  }

  public getUserProfile(): Observable<UserProfile> {
    return this.userProfileSubject.asObservable();
  }

  public setUserProfile(userProfile: UserProfile): void {
    this.userProfileSubject.next(userProfile);
  }

  public isAdmin(userProfile: UserProfile): boolean {
    return userProfile && userProfile.role.toUpperCase() === UserRole.Admin.toUpperCase();
  }

  public areMobileHeadersAvailable(): boolean {
    return this.options.headers.Authorization !== '';
  }

  public getHeaders(): HttpHeaders {
    const token = this.getIdTokenValue();
    const refresh = this.getRefreshTokenValue();
    const bearerToken = this.setIdToken(token);
    this.setRefreshToken(refresh);
    return this.options.headers as unknown as HttpHeaders;
  }

  public getIdTokenValue(): string {
    const cookieIdToken = document.cookie.split(';').find((i) => i.trim().startsWith('id_token='));
    const localStorageToken = window.localStorage.id_token ? window.localStorage.id_token : ``;
    return cookieIdToken
      ? cookieIdToken.trim().replace('id_token=', '')
      : localStorageToken.trim().replace('Bearer ', '');
  }

  public getRefreshTokenValue(): string {
    const cookieRefreshToken = document.cookie.split(';').find((i) => i.trim().startsWith('refresh_token='));
    const localStorageToken = window.localStorage.refresh_token ? window.localStorage.refresh_token : ``;
    return cookieRefreshToken
      ? cookieRefreshToken.trim()
      : localStorageToken.trim()
  }

  private getAuthenticationEndpointUrl(): string {
    let authenticationEndpointUrl: string = this.configurationService.getConfiguration().authenticationEndpointUrl;

    if (!authenticationEndpointUrl.endsWith('/')) {
      authenticationEndpointUrl += '/';
    }

    return authenticationEndpointUrl;
  }

  private getCallBackUrl(): string {
    const callBackUri = this.configurationService.getConfiguration()?.baseUrl;
    let uri: string = callBackUri ? callBackUri: null;
    return uri;
  }

  private setRefreshToken(value: string) {
    window.localStorage.refresh_token = value
      ? value
      : ``;
    return window.localStorage.refresh_token;
  }

  private setIdToken(value: string): string {
    window.localStorage.id_token = value
      ? `Bearer ${value}`
      : ``;
    return window.localStorage.id_token;
  }
}
